import { useMemo } from 'react';
import type { GroupBalance, ParticipantBalance } from '../types';

export interface UnifiedTransfer {
  fromId: string;
  fromName: string;
  fromDisplayName: string;
  fromIsGroup: boolean;
  toId: string;
  toName: string;
  toDisplayName: string;
  toIsGroup: boolean;
  amount: number;
}

export interface TransferUnit {
  id: string;
  name: string;
  displayName: string;
  balance: number;
  isGroup: boolean;
}

// Pure function to calculate minimum transfers between units
export function calculateMinimumTransfers(units: TransferUnit[]): UnifiedTransfer[] {
  const creditors: (TransferUnit & { amount: number })[] = [];
  const debtors: (TransferUnit & { amount: number })[] = [];

  units.forEach((u) => {
    if (u.balance > 0) {
      creditors.push({ ...u, amount: u.balance });
    } else if (u.balance < 0) {
      debtors.push({ ...u, amount: -u.balance });
    }
  });

  // Sort for consistent results (largest first)
  creditors.sort((a, b) => b.amount - a.amount);
  debtors.sort((a, b) => b.amount - a.amount);

  const transfers: UnifiedTransfer[] = [];

  let creditorIndex = 0;
  let debtorIndex = 0;

  while (creditorIndex < creditors.length && debtorIndex < debtors.length) {
    const creditor = creditors[creditorIndex]!;
    const debtor = debtors[debtorIndex]!;

    const transferAmount = Math.min(creditor.amount, debtor.amount);

    if (transferAmount > 0) {
      transfers.push({
        fromId: debtor.id,
        fromName: debtor.name,
        fromDisplayName: debtor.displayName,
        fromIsGroup: debtor.isGroup,
        toId: creditor.id,
        toName: creditor.name,
        toDisplayName: creditor.displayName,
        toIsGroup: creditor.isGroup,
        amount: transferAmount,
      });
    }

    creditor.amount -= transferAmount;
    debtor.amount -= transferAmount;

    if (creditor.amount === 0) creditorIndex++;
    if (debtor.amount === 0) debtorIndex++;
  }

  return transfers;
}

// Pure function to convert balances to transfer units
export function convertToTransferUnits(
  groupBalances: GroupBalance[],
  unassignedBalances: ParticipantBalance[]
): TransferUnit[] {
  // Convert groups to transfer units
  const groupUnits: TransferUnit[] = groupBalances.map((gb) => ({
    id: gb.groupId,
    name: gb.representativeName,
    displayName: gb.groupName,
    balance: gb.totalBalance,
    isGroup: true,
  }));

  // Convert unassigned individuals to transfer units
  const individualUnits: TransferUnit[] = unassignedBalances.map((pb) => ({
    id: pb.participantId,
    name: pb.participantName,
    displayName: pb.participantName,
    balance: pb.balance,
    isGroup: false,
  }));

  return [...groupUnits, ...individualUnits];
}

// Pure function to calculate all transfers
export function calculateAllTransfers(
  groupBalances: GroupBalance[],
  unassignedBalances: ParticipantBalance[]
): UnifiedTransfer[] {
  const allUnits = convertToTransferUnits(groupBalances, unassignedBalances);
  return calculateMinimumTransfers(allUnits);
}

// React hook that wraps the pure functions with memoization
export function useAllTransfers(
  groupBalances: GroupBalance[],
  unassignedBalances: ParticipantBalance[]
): UnifiedTransfer[] {
  return useMemo(
    () => calculateAllTransfers(groupBalances, unassignedBalances),
    [groupBalances, unassignedBalances]
  );
}
